import type { SessionTokens } from "./session";

export class AuthError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

interface VerifiedToken {
  sub: string;
}

function apiBase() {
  const configured = process.env.NEXT_PUBLIC_AUDIENCE_API_URL?.replace(/\/$/, "");
  return configured ? configured : "/api";
}

async function errorMessage(response: Response) {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.length > 0) return body.error;
  } catch {
    // The response was not JSON.
  }
  return "Request failed";
}

export async function login(username: string, password: string): Promise<SessionTokens> {
  const response = await fetch(`${apiBase()}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    throw new AuthError(await errorMessage(response), response.status);
  }

  const body = (await response.json()) as Partial<SessionTokens>;

  if (!body.access || !body.refresh) {
    throw new AuthError("Login did not return a session", response.status);
  }

  return { access: body.access, refresh: body.refresh };
}

export async function refreshAccess(refresh: string) {
  const response = await fetch(`${apiBase()}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    throw new AuthError(await errorMessage(response), response.status);
  }

  const body = (await response.json()) as { access?: string };

  if (!body.access) {
    throw new AuthError("Refresh did not return an access token", response.status);
  }
  
  return body.access;
}

export async function verifyToken(token: string): Promise<VerifiedToken | null> {
  const response = await fetch(`${apiBase()}/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) return null;
  const body = (await response.json()) as Partial<VerifiedToken>;

  if (!body.sub) return null;

  return { sub: body.sub };
}

export async function logout(tokens: SessionTokens) {
  const response = await fetch(`${apiBase()}/auth/logout`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${tokens.access}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh: tokens.refresh }),
  });
  
  if (!response.ok && response.status !== 401) {
    throw new AuthError(await errorMessage(response), response.status);
  }
}
